/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// import React
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import FormHelperText from '@mui/material/FormHelperText';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import leasesData from 'layouts/leases/data/leasesData';
import paymentsData from 'layouts/payments/data/paymentsData';
// import EditLeaseForm from 'examples/EditLease';

import LinearProgress from '@mui/material/LinearProgress';
import EditLeases from "layouts/leases/editLease";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// Helpers
import Helpers from 'utils/helpers';
import AddNewPaymentDialog from "examples/AddNewPaymentDialog";
const ylcId = "2800c706-805e-4cd9-9a64-90df329827b3";
const yl3Id = "c96a2dcb-58c7-4008-9b0d-43c3ee073822";
const gibId = "0ac1e107-d227-45db-a9d6-ca9afd806331";

function Payments () {
  const [errors, setErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tenantCode, setTenantCode] = useState('');
  const [inputValue, setInputValue] = useState("");
  const [filter, setFilter] = useState('');
  const [isShow, setIsShow] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [errorEdit, setErrorEdit] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [pageCustom, setPageCustom] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedValue, setSelectedValue] = useState("all");
  const [isAddPaymentDialogOpen, setIsAddPaymentDialogOpen] = useState(false);
  const isSearched = useRef(false);
  const currentPropertyCode = useRef("All");
  

  const { columns, rows, getPaymentsList, innerLoading, buttonDisabled, setButtonDisabled, open, setOpen, isShowDialog, sortField, sortDirection, setSortField, setSortDirection, editedRow, totalPages, totalRows } = paymentsData();

  const onChangeText = useCallback((event) => {
    const { value }  = event.target
    if (value.length < 3) {
      setErrorMessage('Tenant code value must be more equal than 3 characters');
      setErrors(true);
    } else {
      setErrorMessage('');
      setErrors(false);
    }
    setInputValue(value);
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenNotification(false);
  }, []);

  const onHandleSubmit = useCallback((event) => {
    event.preventDefault();
    setFilter(inputValue);
    isSearched.current = true;
    setPageCustom(0);
  }, [inputValue]);

  const handleAddPaymentClick = useCallback(() => {
    setIsAddPaymentDialogOpen(true);
  }, []);

  const handleAddPaymentDialogClose = useCallback(() => {
    setIsAddPaymentDialogOpen(false);
    // Refresh payments list with default params
    getPaymentsList(1, rowsPerPage, '', 'all', sortField, sortDirection);
  }, [getPaymentsList, rowsPerPage, sortField, sortDirection]);

  const onSortChange = useCallback((sortField, sortDirection) => {
    setSortField(sortField);
    setSortDirection(sortDirection);
    setPageCustom(0);
  }, [getPaymentsList, rowsPerPage, filter]);

  const onChangeSelect = (event) => {
    const { value, name } = event.target;
    if (name === "property_code") {
      setSelectedValue(value);
      if (filter !== "") {
        setFilter('');
        setInputValue('');
      }
    }
  }

  useEffect(() => {
    let currentPage = pageCustom + 1;
    // If the selected value is different from the current property code, reset the page to 0
    if (selectedValue !== currentPropertyCode.current) {
      currentPage = 1;
      currentPropertyCode.current = selectedValue;
      if (pageCustom !== 0) {
        setPageCustom(0);
        return;
      }
    }
    const propertyId = selectedValue === "all" ? "all" : selectedValue;
    getPaymentsList(currentPage, rowsPerPage, filter, propertyId, sortField, sortDirection);
  },[selectedValue, pageCustom, rowsPerPage, filter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Payments
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2} sx={{ padding: "19px 20px"}}>
                  <Grid item xs={9}>
                    <MDBox
                      component="form"
                      display="flex"
                      flexDirection="column"
                      gap={3}
                      role="form"
                      method="POST"
                      onSubmit={onHandleSubmit}
                    >
                      <MDBox mb={2} display="flex" alignItems="center" gap={2}>
                        <MDButton
                          variant="gradient"
                          sx={{
                            padding: "12px 30px",
                            backgroundColor: "#388e3c",
                            color: "#ffffff",
                            borderRadius: "0.2rem",
                            "&:hover": {
                              backgroundColor: "#919191",
                              color: "#fff",
                            },
                            "&:disabled": {
                              background: "#e0e0e0"
                            }
                          }}
                          onClick={handleAddPaymentClick}
                        >
                          Add Payment
                        </MDButton>
                      </MDBox>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={2}
                        justifyContent="space-between"
                        flexWrap="nowrap"
                      >
                        <MDInput
                          type="search"
                          label="Filter"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          sx={{
                            "& .MuiInput-underline:after": { borderBottomColor: "#388e3c"},
                            "& .MuiInputLabel-root.Mui-focused": { color: "#388e3c" }
                          }}
                          value={inputValue}
                          onChange={onChangeText}
                          name="tenant_code"
                        />
                        <MDButton
                          variant="gradient"
                          sx={{
                            padding: "12px 30px",
                            backgroundColor: "#388e3c",
                            color: "#ffffff",
                            borderRadius: "0.2rem",
                            "&:hover": {
                              backgroundColor: "#919191",
                              color: "#fff",
                            },
                            "&:disabled": {
                              background: "#e0e0e0"
                            }
                          }}
                          disabled={ buttonDisabled }
                          type="submit"
                        >
                          Search
                        </MDButton>
                      </MDBox>
                      <MDBox mb={2}>
                          <FormControl width="50%">
                            <InputLabel
                                id="property-code-select-label"
                            >
                                Property code
                            </InputLabel>
                            <Select
                                labelId="property-code-select-label"
                                name="property_code"
                                value={selectedValue}
                                onChange={onChangeSelect}
                                displayEmpty
                                label="Property code"
                                sx={{
                                    minWidth: 200,
                                    height: '44px', // Set chiều cao cố định
                                }}
                                MenuProps={{
                                disableScrollLock: true, // Ngăn MUI tự động thêm padding-right vào <body>
                                }}
                            >
                                <MenuItem value={"all"}>All</MenuItem>
                                <MenuItem value={gibId}>GIB</MenuItem>
                                <MenuItem value={ylcId}>YLC</MenuItem>
                                <MenuItem value={yl3Id}>YL3</MenuItem>
                            </Select>
                          </FormControl>
                        </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox>
                      <DataTable
                          table={{ columns, rows }}
                          isSorted={true}
                          showTotalEntries={false}
                          entriesPerPage={false}
                          isPaginated={true}
                          pageCustom={pageCustom}
                          setPageCustom={setPageCustom}
                          totalPages={totalPages}
                          totalRows={totalRows}
                          rowsPerPage={rowsPerPage}
                          setRowsPerPage={setRowsPerPage}
                          innerLoading={innerLoading}
                          onSortChange={onSortChange}
                          isShow={isShow}
                          noEndBorder
                      />
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        { isShowDialog && (
          <EditLeases
            open={open}
            setOpen={setOpen}
            setOpenNotification={setOpenNotification}
            rowData={editedRow}
            setIsShow={setIsShow}
            setIsEdited={setIsEdited}
            setErrorEdit={setErrorEdit}
          />
        )}
        {isAddPaymentDialogOpen && (
          <AddNewPaymentDialog
            open={isAddPaymentDialogOpen}
            setOpen={setIsAddPaymentDialogOpen}
            onClose={handleAddPaymentDialogClose}
          />
        )}
        <Snackbar open={openNotification} autoHideDuration={4000} onClose={handleClose}>
          { errorEdit ? (
            <Alert
            onClose={handleClose}
            severity="error"
            variant="filled"
            sx={{ width: '100%', color: '#ffffff' }}
          >
            Update Lease Unsuccessfully !
          </Alert>
          ) : (
            <Alert
              onClose={handleClose}
              severity="success"
              variant="filled"
              sx={{ width: '100%', color: '#ffffff' }}
            >
              Update Lease Successfully !
            </Alert>
          )}
      </Snackbar>
    </DashboardLayout>
  );
}
export default Payments;
